import React from 'react';
import { Card, CardContent } from './ui/card';

const Technologies = () => {
  const techCategories = [
    {
      category: 'Programming Languages',
      technologies: [
        { name: 'Java', level: 95 },
        { name: 'Python', level: 92 },
        { name: 'PHP', level: 88 },
        { name: 'JavaScript', level: 90 },
        { name: 'C#', level: 85 },
        { name: 'Go', level: 80 }
      ]
    },
    {
      category: 'AI & Machine Learning',
      technologies: [
        { name: 'TensorFlow', level: 90 },
        { name: 'PyTorch', level: 88 },
        { name: 'Scikit-learn', level: 92 },
        { name: 'OpenAI APIs', level: 85 },
        { name: 'Computer Vision', level: 87 },
        { name: 'NLP', level: 83 }
      ]
    },
    {
      category: 'Cloud & DevOps',
      technologies: [
        { name: 'AWS', level: 93 },
        { name: 'Azure', level: 88 },
        { name: 'Google Cloud', level: 85 },
        { name: 'Docker', level: 90 },
        { name: 'Kubernetes', level: 87 },
        { name: 'Terraform', level: 82 }
      ]
    },
    {
      category: 'Enterprise Solutions',
      technologies: [
        { name: 'SAP', level: 88 },
        { name: 'Oracle', level: 90 },
        { name: 'Salesforce', level: 85 },
        { name: 'Microsoft Dynamics', level: 83 },
        { name: 'ServiceNow', level: 80 },
        { name: 'Workday', level: 78 }
      ]
    }
  ];

  const globalPresence = [
    { region: 'United States', clients: '25+', flag: '🇺🇸' },
    { region: 'United Kingdom', clients: '18+', flag: '🇬🇧' },
    { region: 'European Union', clients: '30+', flag: '🇪🇺' },
    { region: 'MENA Region', clients: '12+', flag: '🌍' }
  ];

  return (
    <section id="technologies" className="py-20 bg-gradient-to-br from-gray-50 via-purple-50/20 to-cyan-50/20 relative overflow-hidden">
      {/* Tech Background Pattern */}
      <div className="absolute inset-0 opacity-8">
        <div className="absolute top-20 right-10 w-80 h-80 border border-cyan-200 rounded-full animate-spin-slow"></div>
        <div className="absolute bottom-20 left-10 w-56 h-56 border border-purple-200 rounded-lg rotate-12 animate-pulse"></div>
        <div className="absolute top-1/3 left-1/3 w-40 h-40 border border-cyan-300/30 rounded-full"></div>
        {/* Circuit pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 3px 3px, rgba(6, 182, 212, 0.03) 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="bg-gradient-to-r from-purple-600 via-cyan-500 to-violet-500 bg-clip-text text-transparent animate-pulse">Expertise</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Cutting-edge technologies and proven expertise across multiple domains to deliver exceptional results.
          </p>
        </div>

        {/* Technologies Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {techCategories.map((category, categoryIndex) => (
            <Card key={categoryIndex} className="shadow-lg border-0 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300 bg-gradient-to-br from-white via-purple-50/30 to-cyan-50/30 border border-cyan-200/50">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-6">{category.category}</h3>
                <div className="space-y-4">
                  {category.technologies.map((tech, techIndex) => (
                    <div key={techIndex} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-800">{tech.name}</span>
                        <span className="text-sm text-purple-600 font-medium">{tech.level}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
                        <div
                          className="bg-gradient-to-r from-purple-500 via-cyan-400 to-violet-400 h-2 rounded-full transition-all duration-1000 ease-out shadow-sm"
                          style={{ width: `${tech.level}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Global Presence */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-cyan-200/50 p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-4">Global Presence</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Serving clients worldwide with localized expertise and 24/7 support across multiple time zones.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {globalPresence.map((location, index) => (
              <div key={index} className="text-center p-6 bg-gradient-to-br from-purple-50/50 via-white to-cyan-50/50 rounded-xl hover:shadow-lg hover:shadow-cyan-500/20 transition-all duration-300 border border-cyan-100 hover:border-cyan-300 transform hover:-translate-y-2">
                <div className="text-4xl mb-3 filter drop-shadow-sm animate-pulse">{location.flag}</div>
                <h4 className="font-semibold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-2">{location.region}</h4>
                <p className="text-2xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text drop-shadow-sm">{location.clients}</p>
                <p className="text-sm text-gray-600">Active Clients</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Technologies;